import { useState, useEffect, useMemo } from "react";
import { FileText, Loader2, Building2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useAdminBillingInfoList } from "@/hooks/useAdminBillingInfoQueries";
import {
  useAdminDefaultEInvoiceTaxConfig,
  useCreateEInvoiceRequest,
} from "@/hooks/useAdminEInvoiceQueries";
import { useAdminOrderDetail } from "@/hooks/useAdminOrders";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import {
  OrderAdminListItem,
  OrderAdminDetail,
} from "@/services/admin-order.service";
import { LineItemsEditor } from "./LineItemsEditor";
import { formatVatVND } from "./e-invoice-utils";
import {
  distributeDiscountAcrossLines,
  getVatFraction,
  getInvoicePaymentMethod,
  formatVND,
  recomputePayload,
} from "./create-einvoice-utils";
import { formatDate, formatDateTime } from "@/lib/utils";
import type {
  EInvoiceCreatePayload,
  EInvoicePayloadBuyer,
  EInvoicePayloadSeller,
  EInvoicePayloadLineItem,
} from "@/types/e-invoice-request";

function getLocalToday(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xs font-medium">{value ?? "—"}</p>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="text-sm font-semibold text-foreground border-b pb-2 mb-3">
      {children}
    </h4>
  );
}

/** Helper to handle number inputs: auto-strips leading zeros and parses empty as 0 */
function handleNumberInput(
  e: React.ChangeEvent<HTMLInputElement>,
  onParsed: (val: number) => void,
) {
  const raw = e.target.value;
  const cleaned = raw.replace(/^0+(?=\d)/, "");
  if (e.target.value !== cleaned) {
    e.target.value = cleaned;
  }
  onParsed(cleaned === "" ? 0 : Number(cleaned));
}

interface CreateEInvoiceRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: OrderAdminListItem | OrderAdminDetail | null;
}

export function CreateEInvoiceRequestDialog({
  open,
  onOpenChange,
  order,
}: CreateEInvoiceRequestDialogProps) {
  const { user: currentUser } = useAdminAuth();
  const {
    data: orderDetail,
    isLoading: isLoadingOrderDetail,
    isError: isOrderError,
    refetch: refetchOrder,
  } = useAdminOrderDetail(order?.id || "");
  const {
    data: billingInfos,
    isError: isBillingError,
    refetch: refetchBilling,
  } = useAdminBillingInfoList(order?.tenantId);
  const createEInvoiceMutation = useCreateEInvoiceRequest();
  const {
    data: taxConfig,
    isFetching: isFetchingTaxConfig,
    isError: isTaxConfigError,
    refetch: refetchTaxConfig,
  } = useAdminDefaultEInvoiceTaxConfig(open && !!order?.tenantId);
  const [initializedOrderId, setInitializedOrderId] = useState<string | null>(
    null,
  );

  const [selectedBillingInfoId, setSelectedBillingInfoId] =
    useState<string>("");
  const [payload, setPayload] = useState<EInvoiceCreatePayload | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Live recomputed payload
  const effectivePayload = useMemo<EInvoiceCreatePayload | null>(() => {
    if (!payload) return null;
    return recomputePayload(payload);
  }, [payload]);

  // Initialize once per opening; background responses must not overwrite edits.
  useEffect(() => {
    if (!open) {
      setInitializedOrderId(null);
      setPayload(null);
      return;
    }
    if (!order || initializedOrderId === order.id) return;
    if (
      !orderDetail ||
      billingInfos === undefined ||
      !taxConfig ||
      isFetchingTaxConfig ||
      isOrderError ||
      isBillingError ||
      isTaxConfigError
    )
      return;

    const primaryInfo =
      billingInfos?.find((b) => b.isPrimary) || billingInfos?.[0];
    if (primaryInfo) {
      setSelectedBillingInfoId(primaryInfo.id);
    } else {
      setSelectedBillingInfoId("");
    }

    const orderSource = orderDetail ?? ("items" in order ? order : null);
    if (!orderSource) return;

    const currency = orderSource.currency || "VND";
    // Match the backend payload builder: completed date first, then placed date.
    const issuedDate =
      orderSource.completedAt?.slice(0, 10) ||
      orderSource.placedAt?.slice(0, 10) ||
      getLocalToday();

    let lineItems: EInvoicePayloadLineItem[] = [];
    const items = orderSource.items;
    const taxRate = orderSource.taxRate ?? 10;
    const vatFraction = getVatFraction(taxRate);

    if (items && items.length > 0) {
      lineItems = items.map((item) => ({
        code: null,
        name: item.description || item.productName || "Product",
        unit: "cai",
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        discountPercentage: 0,
        discountAmount: 0,
        totalAmountWithoutVat: item.totalPrice,
        taxRate: taxRate,
        vatAmount: item.totalPrice * vatFraction,
        totalAmount: item.totalPrice * (1 + vatFraction),
        chatType: "1",
      }));
    } else {
      lineItems = [
        {
          code: null,
          name: `Dịch vụ Đơn hàng ${order.orderNumber}`,
          unit: "cai",
          quantity: 1,
          unitPrice: order.total || 0,
          discountPercentage: 0,
          discountAmount: 0,
          totalAmountWithoutVat: order.total || 0,
          taxRate: taxRate,
          vatAmount: (order.total || 0) * vatFraction,
          totalAmount: (order.total || 0) * (1 + vatFraction),
          chatType: "1",
        },
      ];
    }

    lineItems = distributeDiscountAcrossLines(
      lineItems,
      orderSource.discount || 0,
    );

    const draftBuyer: EInvoicePayloadBuyer = {
      displayName:
        primaryInfo?.contactPerson ||
        primaryInfo?.companyName ||
        order.userFullName ||
        "",
      legalName: primaryInfo?.companyName || "",
      taxCode: primaryInfo?.taxCode || "",
      address: primaryInfo?.address || "",
      email: primaryInfo?.email || order.userEmail || "",
      phone: primaryInfo?.phone || order.userPhoneNumber || "",
      bankAccount: null,
      bankName: null,
    };

    const draftSeller: EInvoicePayloadSeller = {
      code: null,
      name: null,
      address: null,
    };

    const initialDraft: EInvoiceCreatePayload = {
      isSign: false,
      template: taxConfig?.invoiceTemplate || null,
      currencyCode: currency,
      exchangeRate: 1,
      orderNumber: order.orderNumber,
      issuedDate: issuedDate,
      buyer: draftBuyer,
      seller: draftSeller,
      payment: {
        methodCode: getInvoicePaymentMethod(orderSource.payment?.method)
          .methodCode,
        discountAmount: orderSource.discount || 0,
        totalAmountWithoutVat: orderSource.subtotal || order.total,
        vatAmount: orderSource.tax || 0,
        totalAmount: order.total,
      },
      delivery: {
        deliveryFee: 0,
        deliveryPercentage: 0,
      },
      details: lineItems,
    };

    setPayload(recomputePayload(initialDraft));
    setValidationError(null);
    setInitializedOrderId(order.id);
  }, [
    open,
    order,
    orderDetail,
    billingInfos,
    taxConfig,
    initializedOrderId,
    isFetchingTaxConfig,
    isOrderError,
    isBillingError,
    isTaxConfigError,
  ]);

  const handleSelectBillingInfo = (infoId: string) => {
    setSelectedBillingInfoId(infoId);
    if (!payload) return;

    const info = billingInfos?.find((b) => b.id === infoId);
    if (info) {
      setPayload({
        ...payload,
        buyer: {
          ...payload.buyer,
          legalName: info.companyName || "",
          taxCode: info.taxCode || "",
          address: info.address || "",
          email: info.email || order?.userEmail || "",
          phone: info.phone || order?.userPhoneNumber || "",
          displayName: info.contactPerson || info.companyName || "",
        },
      });
    }
  };

  function updateBuyer<K extends keyof EInvoicePayloadBuyer>(
    field: K,
    value: EInvoicePayloadBuyer[K],
  ) {
    if (!payload) return;
    setPayload({
      ...payload,
      buyer: { ...payload.buyer, [field]: value },
    });
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!order || !effectivePayload || initializedOrderId !== order.id) return;

    const buyer = effectivePayload.buyer;
    if (
      !buyer.legalName?.trim() ||
      !buyer.taxCode?.trim() ||
      !buyer.address?.trim() ||
      !buyer.email?.trim()
    ) {
      setValidationError(
        "Please fill in Legal Name, Tax Code, Email, and Address.",
      );
      return;
    }

    if (
      !effectivePayload.issuedDate ||
      Number.isNaN(Date.parse(effectivePayload.issuedDate))
    ) {
      setValidationError("A valid invoice date is required.");
      return;
    }
    if (
      !effectivePayload.details.length ||
      effectivePayload.details.some(
        (line) =>
          !line.name.trim() ||
          !Number.isFinite(line.quantity) ||
          line.quantity <= 0,
      )
    ) {
      setValidationError(
        "Each invoice line requires a name and a quantity greater than zero.",
      );
      return;
    }
    if (createEInvoiceMutation.isPending) return;
    setValidationError(null);

    createEInvoiceMutation.mutate(
      { orderId: order.id, payload: effectivePayload },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      },
    );
  };

  if (!order) return null;

  if (initializedOrderId !== order.id || !effectivePayload) {
    const hasError = isOrderError || isBillingError || isTaxConfigError;
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Create E-Invoice Request #{order.orderNumber}
            </DialogTitle>
            <DialogDescription>
              Preparing invoice data and configuration.
            </DialogDescription>
          </DialogHeader>
          {hasError ? (
            <div className="space-y-3">
              <p role="alert">
                Could not load invoice data or Wi-Invoice configuration.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (isOrderError) void refetchOrder();
                  if (isBillingError) void refetchBilling();
                  if (isTaxConfigError) void refetchTaxConfig();
                }}
              >
                Retry
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 py-4 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading invoice
              data...
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  const templateLabel = taxConfig?.invoiceTemplatesOptions?.find(
    (option) => option.value === effectivePayload.template,
  )?.label;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] flex flex-col p-0 gap-0 overflow-hidden min-w-0">
        <form
          onSubmit={handleSubmit}
          className="flex flex-col h-full min-h-0 w-full min-w-0"
        >
          <DialogHeader className="px-5 py-3 border-b bg-muted/30 shrink-0 text-left">
            <DialogTitle className="text-base font-semibold">
              Create E-Invoice Request #{order.orderNumber}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mt-0.5">
              Review buyer information and invoice lines before creating a
              request for approval.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 p-5 pt-4 overflow-y-auto max-h-[calc(92vh-110px)] min-w-0 flex-1">
            {/* Top Meta Bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/20 p-3 text-xs">
              <div className="flex flex-wrap items-center gap-6">
                <Field
                  label="Status"
                  value={
                    <span className="inline-flex items-center rounded-md bg-amber-500/10 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-300 border border-amber-500/20">
                      Pending
                    </span>
                  }
                />
                <Field
                  label="Tenant"
                  value={
                    <span className="font-semibold">
                      {order.tenantName ?? "—"}
                    </span>
                  }
                />
                <Field
                  label="Requested By"
                  value={
                    <span className="font-medium text-foreground">
                      {currentUser?.email ?? order.userEmail ?? "—"}
                    </span>
                  }
                />
                <Field
                  label="Requested At"
                  value={formatDateTime(new Date())}
                />
              </div>
              <div className="min-w-0 lg:ml-auto">
                <Field
                  label="Template"
                  value={
                    effectivePayload.template
                      ? [effectivePayload.template, templateLabel]
                          .filter(Boolean)
                          .join(" — ")
                      : "Default"
                  }
                />
              </div>
            </div>

            {validationError && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive font-medium break-words">
                {validationError}
              </div>
            )}

            {isLoadingOrderDetail && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground italic">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading complete order items...
              </div>
            )}

            {effectivePayload && (
              /* Main 2-Column Layout Grid matching EInvoiceRequestDetailDialog exactly */
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                {/* ─── LEFT COLUMN (2/3 Width) ────────────────────────────────── */}
                <div className="lg:col-span-2 space-y-5 min-w-0">
                  {/* Saved Tenant Billing Profile Selector */}
                  {billingInfos && billingInfos.length > 0 && (
                    <div className="space-y-1.5 rounded-lg border bg-muted/30 p-3.5 w-full min-w-0">
                      <Label className="text-xs font-semibold flex items-center gap-1.5 text-muted-foreground mb-1">
                        <Building2 className="h-3.5 w-3.5 shrink-0" />
                        Saved Tenant Billing Profile
                      </Label>
                      <Select
                        value={selectedBillingInfoId}
                        onValueChange={handleSelectBillingInfo}
                      >
                        <SelectTrigger className="h-9 text-xs bg-background w-full min-w-0 max-w-full [&>span]:truncate [&>span]:block [&>span]:w-full [&>span]:text-left">
                          <SelectValue placeholder="Select a billing profile" />
                        </SelectTrigger>
                        <SelectContent className="max-w-[560px]">
                          {billingInfos.map((info) => (
                            <SelectItem
                              key={info.id}
                              value={info.id}
                              className="text-xs"
                            >
                              <span className="truncate block max-w-[500px]">
                                {info.companyName} ({info.taxCode})
                                {info.isPrimary ? " - Primary" : ""}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Buyer Information Card */}
                  <div className="rounded-lg border bg-card p-4 space-y-3 shadow-xs">
                    <SectionTitle>Buyer Information</SectionTitle>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 text-xs">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-foreground/90">
                          Display Name
                        </Label>
                        <Input
                          value={effectivePayload.buyer.displayName ?? ""}
                          onChange={(e) =>
                            updateBuyer("displayName", e.target.value)
                          }
                          className="h-9 text-xs"
                          placeholder="e.g. TRƯƠNG HUY TRƯỜNG"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-foreground/90">
                          Legal Name <span className="text-rose-500">*</span>
                        </Label>
                        <Input
                          value={effectivePayload.buyer.legalName ?? ""}
                          onChange={(e) =>
                            updateBuyer("legalName", e.target.value || null)
                          }
                          className="h-9 text-xs"
                          placeholder="CÔNG TY CỔ PHẦN CÔNG NGHỆ..."
                          required
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-foreground/90">
                          Tax Code <span className="text-rose-500">*</span>
                        </Label>
                        <Input
                          value={effectivePayload.buyer.taxCode ?? ""}
                          onChange={(e) =>
                            updateBuyer("taxCode", e.target.value || null)
                          }
                          className="h-9 text-xs font-mono"
                          placeholder="0311174986"
                          required
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-foreground/90">
                          Email <span className="text-rose-500">*</span>
                        </Label>
                        <Input
                          type="email"
                          value={effectivePayload.buyer.email ?? ""}
                          onChange={(e) =>
                            updateBuyer("email", e.target.value || null)
                          }
                          className="h-9 text-xs"
                          placeholder="tmt@tmtco.asia"
                          required
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-foreground/90">
                          Phone
                        </Label>
                        <Input
                          value={effectivePayload.buyer.phone ?? ""}
                          onChange={(e) =>
                            updateBuyer("phone", e.target.value || null)
                          }
                          className="h-9 text-xs"
                          placeholder="0908075455"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-foreground/90">
                          Bank Account
                        </Label>
                        <Input
                          value={effectivePayload.buyer.bankAccount ?? ""}
                          onChange={(e) =>
                            updateBuyer("bankAccount", e.target.value || null)
                          }
                          className="h-9 text-xs font-mono"
                          placeholder="Account number"
                        />
                      </div>
                      <div className="sm:col-span-2 space-y-1.5">
                        <Label className="text-xs font-medium text-foreground/90">
                          Bank Name
                        </Label>
                        <Input
                          value={effectivePayload.buyer.bankName ?? ""}
                          onChange={(e) =>
                            updateBuyer("bankName", e.target.value || null)
                          }
                          className="h-9 text-xs"
                          placeholder="e.g. Vietcombank, Techcombank"
                        />
                      </div>
                      <div className="sm:col-span-2 space-y-1.5">
                        <Label className="text-xs font-medium text-foreground/90">
                          Address <span className="text-rose-500">*</span>
                        </Label>
                        <Input
                          value={effectivePayload.buyer.address ?? ""}
                          onChange={(e) =>
                            updateBuyer("address", e.target.value || null)
                          }
                          className="h-9 text-xs"
                          placeholder="Company legal address"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Line Items Details Table */}
                  <div className="rounded-lg border bg-card p-4 space-y-3 shadow-xs">
                    <SectionTitle>Invoice Line Items (Details)</SectionTitle>
                    <LineItemsEditor
                      items={effectivePayload.details}
                      saleItems={orderDetail?.items}
                      onChange={(items) =>
                        setPayload(
                          recomputePayload({
                            ...effectivePayload,
                            details: items,
                          }),
                        )
                      }
                    />
                  </div>
                </div>

                <aside className="lg:col-span-1 space-y-5 rounded-lg border bg-muted/30 p-4 min-w-0">
                  <div className="space-y-1">
                    <h3 className="text-sm font-semibold break-words">
                      Invoice #{order.orderNumber}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Issued Date:{" "}
                      {effectivePayload.issuedDate
                        ? formatDate(new Date(effectivePayload.issuedDate))
                        : "—"}
                    </p>
                  </div>

                  <section className="space-y-3">
                    <SectionTitle>Delivery</SectionTitle>
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="create-invoice-delivery-fee"
                        className="text-xs"
                      >
                        Delivery Fee ({effectivePayload.currencyCode})
                      </Label>
                      <Input
                        id="create-invoice-delivery-fee"
                        type="number"
                        step="0.000001"
                        min={0}
                        value={effectivePayload.delivery?.deliveryFee ?? 0}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) =>
                          handleNumberInput(e, (value) =>
                            setPayload(
                              recomputePayload({
                                ...effectivePayload,
                                delivery: {
                                  ...effectivePayload.delivery,
                                  deliveryFee: value,
                                },
                              }),
                            ),
                          )
                        }
                        className="h-9 text-xs bg-background"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="create-invoice-delivery-vat"
                        className="text-xs"
                      >
                        Delivery VAT (%)
                      </Label>
                      <Input
                        id="create-invoice-delivery-vat"
                        type="number"
                        step="0.000001"
                        min={0}
                        max={100}
                        value={
                          effectivePayload.delivery?.deliveryPercentage ?? 0
                        }
                        onFocus={(e) => e.target.select()}
                        onChange={(e) =>
                          handleNumberInput(e, (value) =>
                            setPayload(
                              recomputePayload({
                                ...effectivePayload,
                                delivery: {
                                  deliveryFee:
                                    effectivePayload.delivery?.deliveryFee ?? 0,
                                  deliveryPercentage: value,
                                },
                              }),
                            ),
                          )
                        }
                        className="h-9 text-xs bg-background"
                      />
                    </div>
                  </section>

                  <section className="space-y-3">
                    <SectionTitle>Totals Summary</SectionTitle>
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="create-invoice-discount"
                        className="text-xs"
                      >
                        Discount Amount ({effectivePayload.currencyCode})
                      </Label>
                      <Input
                        id="create-invoice-discount"
                        type="number"
                        step="0.000001"
                        min={0}
                        value={effectivePayload.payment.discountAmount}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) =>
                          handleNumberInput(e, (value) =>
                            setPayload(
                              recomputePayload({
                                ...effectivePayload,
                                details: distributeDiscountAcrossLines(
                                  effectivePayload.details,
                                  value,
                                ),
                              }),
                            ),
                          )
                        }
                        className="h-9 text-xs bg-background"
                      />
                    </div>
                    <div className="rounded-md border bg-background p-3 space-y-2 text-xs">
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">
                          Subtotal (excl. VAT)
                        </span>
                        <span className="text-right">
                          {formatVND(
                            effectivePayload.payment.totalAmountWithoutVat,
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">VAT</span>
                        <span className="text-right">
                          {formatVatVND(effectivePayload.payment.vatAmount)}
                        </span>
                      </div>
                      <Separator />
                      <div className="flex justify-between gap-2 font-semibold">
                        <span>Total Amount</span>
                        <span className="text-right text-rose-600 dark:text-rose-400">
                          {formatVND(effectivePayload.payment.totalAmount)}
                        </span>
                      </div>
                    </div>
                    <p className="rounded-md bg-amber-500/10 p-3 text-xs text-muted-foreground">
                      Amounts are calculated from the invoice lines and delivery
                      charges.
                    </p>
                  </section>

                  <section className="space-y-3">
                    <SectionTitle>Payment Information</SectionTitle>
                    <div className="flex justify-between gap-2 text-xs">
                      <span className="text-muted-foreground">
                        Payment Method
                      </span>
                      <span className="font-medium text-right">
                        {
                          getInvoicePaymentMethod(orderDetail?.payment?.method)
                            .label
                        }
                      </span>
                    </div>
                    <div className="flex justify-between gap-2 text-xs">
                      <span className="text-muted-foreground">Currency</span>
                      <span className="font-medium">
                        {effectivePayload.currencyCode}
                      </span>
                    </div>
                  </section>
                </aside>
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-2 border-t px-5 py-3 shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={createEInvoiceMutation.isPending}
              className="h-9 px-4 text-xs"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={createEInvoiceMutation.isPending}
              className="h-9 px-4 text-xs bg-rose-600 hover:bg-rose-700 text-white font-medium"
            >
              {createEInvoiceMutation.isPending ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FileText className="mr-1.5 h-3.5 w-3.5" />
                  Create E-Invoice Request
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
